import { z } from "zod";
import { recordSofaAnomaly } from "@/utils/sofaDiagnostics";

/**
 * Runtime schema validation for SOFA feed releases.
 *
 * Guarantees that every release passed downstream has non-empty string
 * `ProductName` and `ProductVersion` fields. Anything else is rejected
 * (and recorded as a diagnostic anomaly) so vulnerability filters never
 * see undefined or wrongly-typed identifiers.
 */

const NonEmptyString = z
  .string()
  .trim()
  .min(1, "must be a non-empty string");

const CVEDetailSchema = z
  .object({
    nist_url: z.string().optional(),
    actively_exploited: z.boolean().optional(),
    in_kev: z.boolean().optional(),
    severity: z.string().optional(),
  })
  .passthrough();

/**
 * Strict release schema — REQUIRES ProductName and ProductVersion.
 * Other fields are coerced to safe defaults via `.catch(...)` so a single
 * malformed sub-field doesn't reject an otherwise-usable release.
 */
export const SofaSecurityReleaseSchema = z
  .object({
    ProductName: NonEmptyString,
    ProductVersion: NonEmptyString,
    UpdateName: z.string().catch(""),
    Build: z.string().catch(""),
    ReleaseDate: z.string().catch(""),
    ReleaseType: z.string().catch(""),
    SecurityInfo: z.string().catch(""),
    SupportedDevices: z.array(z.string()).catch([]),
    CVEs: z.record(z.string(), CVEDetailSchema).catch({}),
    ActivelyExploitedCVEs: z.array(z.string()).catch([]),
    UniqueCVEsCount: z.number().catch(0),
    DeviceScope: z.string().catch("universal"),
    update_summary: z.unknown().optional(),
    UpdateSummary: z.unknown().optional(),
  })
  .passthrough();

export type ValidatedRelease = z.infer<typeof SofaSecurityReleaseSchema>;

/**
 * Validate a single release. Returns the parsed release on success, or null
 * if it fails the contract (logging an anomaly for diagnostics).
 */
export function validateRelease(
  release: unknown,
  source = "validateRelease"
): ValidatedRelease | null {
  const result = SofaSecurityReleaseSchema.safeParse(release);
  if (!result.success) {
    const reason = result.error.issues
      .map(i => `${i.path.join(".") || "(root)"}: ${i.message}`)
      .join("; ");
    recordSofaAnomaly(source, `schema validation failed — ${reason}`, release);
    return null;
  }
  return result.data;
}

/**
 * Validate a list of releases, dropping any that fail the schema.
 * Always returns an array — never undefined — so downstream `.map`/`.filter`
 * calls are safe.
 */
export function validateReleases(
  releases: unknown,
  source = "validateReleases"
): ValidatedRelease[] {
  if (!Array.isArray(releases)) {
    if (releases !== undefined && releases !== null) {
      recordSofaAnomaly(source, "expected array of releases", releases);
    }
    return [];
  }
  const valid: ValidatedRelease[] = [];
  for (const r of releases) {
    const parsed = validateRelease(r, source);
    if (parsed) valid.push(parsed);
  }
  return valid;
}
